import { getApiErrorMessage } from '@/utils/utils';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import { Box, Button, Paper, Typography } from '@mui/material';
import type { FC } from 'react';

interface ErrorDisplayProps {
  error: Error;
  title?: string | undefined;
  onRetry?: (() => void) | undefined;
}

const GENERIC_FALLBACK = 'An unexpected error occurred. Please try again later.';

// REQ-14: every query-state error screen reads the API's own RFC 7807 detail through the one
// normalisation point (getApiErrorMessage) instead of `error.message` — a real AxiosError's own
// `.message` is always axios's generic transport sentence ("Request failed with status code 404"),
// never what the API said, which is exactly the "generic error" the switch's non-functional
// requirement (apps/ui/README.md § Error Handling) forbids.
const ErrorDisplay: FC<ErrorDisplayProps> = ({
  error,
  title = 'Something went wrong',
  onRetry
}) => {
  const isNetworkError =
    error.message.toLowerCase().includes('network') ||
    error.message.toLowerCase().includes('connection') ||
    error.message.toLowerCase().includes('offline');

  const Icon = isNetworkError ? WifiOffIcon : ErrorOutlineIcon;

  return (
    <Paper
      elevation={0}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 4,
        backgroundColor: 'error.light',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'error.main',
        opacity: 0.95
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          backgroundColor: 'error.main',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2
        }}
      >
        <Icon sx={{ fontSize: 32, color: 'white' }} />
      </Box>

      <Typography variant="h2" color="error.dark" gutterBottom textAlign="center">
        {title}
      </Typography>

      <Typography
        variant="body1"
        color="error.dark"
        textAlign="center"
        sx={{ mb: 3, maxWidth: 400, opacity: 0.85 }}
      >
        {isNetworkError
          ? 'Unable to connect to the server. Please check your internet connection and try again.'
          : getApiErrorMessage(error, GENERIC_FALLBACK)}
      </Typography>

      {onRetry && (
        <Button
          variant="contained"
          color="error"
          startIcon={<RefreshIcon />}
          onClick={onRetry}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 3
          }}
        >
          Try Again
        </Button>
      )}
    </Paper>
  );
};

export default ErrorDisplay;
